import { isTickmillEU } from '@utils/companyName.utils'

import {
  AccountDetailedDto,
  ClientProductState,
  ProductStatus,
  isAccountDetailedClosedStatus,
  isAccountDetailedDormant,
  isAccountDetailedDormantStatus,
} from '@model/AccountDetailedDto'
import { BaseProduct, TickmillProductType } from '@model/TickmillProductType'

import { isAccountEmailVerified } from './accountEmailStatuses'

export const isProductAppropriatenessInvalid = (
  product: TickmillProductType,
  account: AccountDetailedDto | undefined
): boolean => {
  if (!account) {
    return false
  }

  return (
    !hasClearedAppropriatenessTest(account, product) ||
    isProductAppropriatenessTestFailed(product, account) ||
    hasProductDormantStatus(account, product) ||
    hasProductClosedStatus(account, product) ||
    hasProductEmailVerifiedStatus(account, product)
  )
}

export const isProductAppropriatenessTestFailed = (
  product: TickmillProductType,
  account: AccountDetailedDto | undefined
): boolean => {
  return (
    hasProductAppropriatenessTestSubmittedStatus(account, product) &&
    !hasClearedAppropriatenessTest(account, product)
  )
}

export const showUnsuccessfulRegistrationScreen = (
  product: TickmillProductType,
  account: AccountDetailedDto | undefined
): boolean => {
  return (
    hasProductAppropriatenessTestSubmittedStatus(account, product) &&
    !hasClearedAppropriatenessTest(account, product) &&
    isTickmillEU(account)
  )
}

export const isProductAppropriatenessTestNotFailed = (
  product: TickmillProductType,
  account: AccountDetailedDto | undefined
): boolean => {
  return (
    hasProductAppropriatenessTestSubmittedStatus(account, product) &&
    hasClearedAppropriatenessTest(account, product)
  )
}

export const hasClearedAppropriatenessTest = (
  account: AccountDetailedDto | undefined,
  tickmillProductType: TickmillProductType
): boolean => {
  return evaluateProductStateAttribute(
    account,
    tickmillProductType,
    (productState) => productState.passedAppropriatenessTest
  )
}

export const isAppropriatenessTestMaximumRetakeLimitReached = (
  account: AccountDetailedDto | undefined,
  product: TickmillProductType
): boolean => {
  if (!account) {
    return false
  }

  const productState = account.clientProductStates?.find(
    (state) => state.tickmillProduct.id === product
  )

  return !!productState?.appropriatenessTestMaximumRetakeLimitReached
}

export const showApptestRiskScreen = (
  account: AccountDetailedDto | undefined,
  product: TickmillProductType
): boolean => {
  return evaluateProductStateAttribute(
    account,
    product,
    (productState) => !!productState.showApptestRiskScreen
  )
}

export const getApptestRiskScreenProduct = (
  account: AccountDetailedDto | undefined,
  preferredProduct?: TickmillProductType
): TickmillProductType | undefined => {
  if (preferredProduct && showApptestRiskScreen(account, preferredProduct)) {
    return preferredProduct
  }

  return account?.clientProductStates
    ?.filter((p) => isBaseProduct(p.tickmillProduct.id))
    .find((productState) => !!productState.showApptestRiskScreen)?.tickmillProduct.id
}

export const hasSubmittedAppropriatenessTest = (
  account: AccountDetailedDto | undefined,
  product: TickmillProductType
): boolean => {
  return evaluateProductStateAttribute(
    account,
    product,
    (productState) => productState.submittedAppropriatenessTest
  )
}

export const hasSubmittedEconomicProfileTest = (
  account: AccountDetailedDto | undefined,
  product: TickmillProductType | undefined
) => {
  return evaluateProductStateAttribute(
    account,
    product,
    (productState) => productState.submittedEconomicProfileTest
  )
}

const isEconomicProfileProduct = (product: TickmillProductType | undefined): boolean => {
  return product === TickmillProductType.CFD || product === TickmillProductType.ETD
}

export const isEconomicProfileTestNeeded = (
  account: AccountDetailedDto | undefined,
  product: TickmillProductType | undefined
): boolean => {
  return (
    isTickmillEU(account) &&
    isEconomicProfileProduct(product) &&
    !hasSubmittedEconomicProfileTest(account, product)
  )
}

export const hasProductEmailVerifiedStatus = (
  account: AccountDetailedDto | undefined,
  product: TickmillProductType | undefined
): boolean => {
  return evaluateProductStateAttribute(
    account,
    product,
    (productState) => productState.calculatedStatus.id === ProductStatus.Unactive
  )
}

export const hasProductDormantStatus = (
  account: AccountDetailedDto | undefined,
  product: TickmillProductType | undefined
): boolean => {
  return evaluateProductStateAttribute(account, product, (productState) =>
    isAccountDetailedDormantStatus(productState.calculatedStatus.id)
  )
}

export const hasProductClosedStatus = (
  account: AccountDetailedDto | undefined,
  product: TickmillProductType
): boolean => {
  return evaluateProductStateAttribute(account, product, (productState) =>
    isAccountDetailedClosedStatus(productState.calculatedStatus.id)
  )
}

export const hasProductAppropriatenessTestSubmittedStatus = (
  account: AccountDetailedDto | undefined,
  product: TickmillProductType | undefined
): boolean => {
  return evaluateProductStateAttribute(
    account,
    product,
    (productState) => productState.calculatedStatus.id === ProductStatus.TestSubmitted
  )
}

export const hasProductValidationDeniedStatus = (
  account: AccountDetailedDto | undefined,
  product: TickmillProductType | undefined
): boolean => {
  return evaluateProductStateAttribute(
    account,
    product,
    (productState) => productState.calculatedStatus.id === ProductStatus.ValidationDenied
  )
}

export const hasProductActivatedStatus = (
  account: AccountDetailedDto | undefined,
  productType: TickmillProductType | undefined
): boolean => {
  return evaluateProductStateAttribute(
    account,
    productType,
    (productState) => productState.calculatedStatus.id === ProductStatus.Activated
  )
}

export const isProductRegistered = (
  account: AccountDetailedDto | undefined,
  productType: TickmillProductType
): boolean => {
  return evaluateProductStateAttribute(account, productType, () => true)
}

export const isTargetProductRegistered = (
  account: AccountDetailedDto | undefined,
  isProductTypeCFD: boolean
): boolean => {
  if (!account) {
    return false
  }

  const targetProduct = isProductTypeCFD ? TickmillProductType.ETD : TickmillProductType.CFD
  return isProductRegistered(account, targetProduct)
}

export const isBaseProduct = (product: TickmillProductType): product is BaseProduct => {
  return product === TickmillProductType.CFD || product === TickmillProductType.ETD
}

export const getAlternativeRegisteredBaseProduct = (
  account: AccountDetailedDto | undefined,
  currentProduct: TickmillProductType
): TickmillProductType | undefined => {
  if (!account) return undefined

  const registeredProducts = Array.from(
    new Set<TickmillProductType>([
      ...(account.clientProductStates ?? []).map((productState) => productState.tickmillProduct.id),
      ...(account.tickmillProducts ?? []).map((product) => product.id as TickmillProductType),
    ])
  )

  const preferredProduct = account.preferredLandingPage?.tickmillProductId
  if (
    preferredProduct &&
    preferredProduct !== currentProduct &&
    registeredProducts.includes(preferredProduct) &&
    isBaseProduct(preferredProduct)
  ) {
    return preferredProduct
  }

  return registeredProducts.find(
    (productId) => productId !== currentProduct && isBaseProduct(productId)
  )
}

export const determineProductState = (
  account: AccountDetailedDto | undefined,
  productType: TickmillProductType | undefined
): ClientProductState | undefined => {
  if (!account) {
    return undefined
  }

  return account.clientProductStates?.find(
    (productState) => productState.tickmillProduct.id === productType
  )
}

export const evaluateProductStateAttribute = (
  account: AccountDetailedDto | undefined,
  productType: TickmillProductType | undefined,
  attributeEvaluator: (productState: ClientProductState) => boolean
): boolean => {
  const productState = determineProductState(account, productType)
  return productState ? attributeEvaluator(productState) : false
}

export const isAccountAppropriatenessTestAllow = (
  accountDetailed: AccountDetailedDto | undefined,
  productType: TickmillProductType | undefined
) => {
  if (accountDetailed === undefined || accountDetailed === null) {
    return false
  }

  return (
    isAccountProductTestFailed(accountDetailed, productType) ||
    isAccountDetailedDormant(accountDetailed) ||
    isAccountDetailedClosedStatus(accountDetailed.status.id) ||
    isAccountEmailVerified(accountDetailed)
  )
}

export const isAccountProductTestFailed = (
  account: AccountDetailedDto | undefined,
  productType: TickmillProductType | undefined
) => {
  return evaluateProductStateAttribute(
    account,
    productType,
    (productState) =>
      productState.calculatedStatus.id === ProductStatus.TestSubmitted &&
      !productState.passedAppropriatenessTest
  )
}

export const isProductMarketDataClassificationSet = (
  account: AccountDetailedDto | undefined,
  productType: TickmillProductType | undefined
) =>
  evaluateProductStateAttribute(
    account,
    productType,
    (productState) => productState.setMarketDataClassification
  )
